/* grove-blog-entry.js — The Grove Line blog entry behaviour.
   Vanilla web components, no inline handlers. */

/*
  <grove-blog-lightbox> — full-screen photo viewer for a blog entry
  (snippets/grove-blog-lightbox.liquid). Openers are the photos themselves
  (snippets/grove-blog-entry-image.liquid: a <button data-grove-lightbox="N">
  around each <img>); slide N is the N-th photo on the page.

  A native <dialog> + showModal(), like the header panels and the PDP size
  guide: focus trap, Esc and focus restoration are the browser's. What lives
  here: which slide is active (.is-active), arrows that stop at the ends rather
  than wrap (owner decision 2026-08-23 for the PDP; same here), arrow keys, a
  swipe, the body scroll lock and eager-loading the neighbours of the active
  slide so the next tap has its photo ready.

  One document-level click handler serves every entry on the page and survives
  Theme Editor section re-renders (which do not re-run inline scripts).
*/
if (!customElements.get('grove-blog-lightbox')) {
  customElements.define(
    'grove-blog-lightbox',
    class GroveBlogLightbox extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.content = this.dialog.querySelector('.grove-blog-lightbox__content');
        this.slides = Array.from(this.dialog.querySelectorAll('[data-lightbox-slide]'));
        this.prevArrow = this.dialog.querySelector('[data-lightbox-prev]');
        this.nextArrow = this.dialog.querySelector('[data-lightbox-next]');
        this.currentLabel = this.dialog.querySelector('[data-lightbox-current]');
        this.totalLabel = this.dialog.querySelector('[data-lightbox-total]');
        this.index = 0;

        this.dialog.addEventListener('click', this.onClick.bind(this));
        this.dialog.addEventListener('keydown', this.onKeydown.bind(this));
        this.dialog.addEventListener('close', this.onClose.bind(this));
        this.dialog.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.dialog.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.dialog.addEventListener('pointercancel', () => { this.start = null; });

        if (!GroveBlogLightbox.delegated) {
          GroveBlogLightbox.delegated = true;
          document.addEventListener('click', (event) => {
            const opener = event.target.closest('[data-grove-lightbox]');
            if (!opener) return;
            const dialog = document.getElementById(opener.getAttribute('aria-controls'));
            const host = dialog ? dialog.closest('grove-blog-lightbox') : null;
            if (host) host.open(Number(opener.dataset.groveLightbox) - 1);
          });
        }
      }

      disconnectedCallback() {
        // The Theme Editor swaps the section out from under an open dialog.
        if (this.dialog && this.dialog.open) document.body.classList.remove('overflow-hidden');
      }

      open(index) {
        if (this.dialog.open || this.slides.length === 0) return;
        this.show(index);
        this.dialog.showModal();
        // Dawn's class (base.css): the page behind must not scroll under the photo.
        document.body.classList.add('overflow-hidden');
        /* Park focus on the content box, not on the X: Safari paints :focus-visible for
           any programmatic focus, so a mouse user would otherwise see a ring around the
           X the moment the viewer opens (same fix as the PDP size guide). */
        if (this.content) this.content.focus({ preventScroll: true });
      }

      show(index) {
        const last = this.slides.length - 1;
        this.index = Math.min(last, Math.max(0, index));

        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));

        if (this.currentLabel) this.currentLabel.textContent = this.index + 1;
        if (this.totalLabel) this.totalLabel.textContent = this.slides.length;

        // Stop and grey out at the ends; no wrap.
        if (this.prevArrow) this.prevArrow.toggleAttribute('disabled', this.index === 0);
        if (this.nextArrow) this.nextArrow.toggleAttribute('disabled', this.index === last);

        this.prime();
      }

      /* Slides are lazy and a hidden slide has no box, so nothing loads until it is shown.
         Switching the neighbours to eager starts their fetch now; the next tap has its
         photo ready instead of a blank frame. */
      prime() {
        [this.index - 1, this.index, this.index + 1].forEach((i) => {
          const img = this.slides[i] && this.slides[i].querySelector('img');
          if (img && img.loading === 'lazy') img.loading = 'eager';
        });
      }

      step(direction) {
        const to = this.index + direction;
        if (to < 0 || to > this.slides.length - 1) return;
        this.show(to);
      }

      onClick(event) {
        if (event.target.closest('[data-lightbox-close]')) {
          this.dialog.close();
          return;
        }
        if (event.target.closest('[data-lightbox-prev]')) {
          this.step(-1);
          return;
        }
        if (event.target.closest('[data-lightbox-next]')) {
          this.step(1);
          return;
        }
        // The letterbox around the photo is the ground: a click there closes.
        // A click on the photo itself does nothing (a missed arrow tap must not dismiss).
        if (event.target.closest('img, button')) return;
        this.dialog.close();
      }

      onKeydown(event) {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        }
      }

      /*
        The swipe (same rule as the PDP gallery): a pointer that travels further sideways
        than down is a page turn; anything else is left to the browser. No preventDefault
        anywhere — the browser keeps ownership of the gesture.
      */
      onPointerDown(event) {
        this.start = { x: event.clientX, y: event.clientY };
      }

      onPointerUp(event) {
        if (!this.start) return;
        const dx = event.clientX - this.start.x;
        const dy = event.clientY - this.start.y;
        this.start = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        this.step(dx < 0 ? 1 : -1);
      }

      onClose() {
        document.body.classList.remove('overflow-hidden');
      }
    }
  );
}
